import { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box, Paper, Typography, TextField, Button, IconButton,
    Autocomplete, CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate, useParams } from 'react-router-dom';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
    getAllTutorials, createTutorial, updateTutorial, uploadTutorialImage, uploadTutorialFile,
} from '../../api/tutorialApi';
import { findSimilarTitles } from './findSimilar';

const PP = "'Poppins', sans-serif";
const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '') || '';

const EMPTY_FORM = {
    tutorialId: 0,
    category: '',
    subCategory: '',
    title: '',
    titleEn: '',
    titleMy: '',
    description: '',
    descriptionEn: '',
    descriptionMy: '',
    imageUrls: '',
    coverUrl: '',
    attachments: '',
    videoLinksCn: '',
    videoLinksEn: '',
    videoLinksMy: '',
    sortOrder: 0,
};

function parseAttachments(raw) {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((a) => a && a.url) : [];
    } catch {
        return [];
    }
}

function resolveImageUrl(url) {
    if (!url) return url;
    return url.startsWith('/') ? `${API_BASE}${url}` : url;
}

function parseVideoLinks(raw) {
    if (!raw) return { cn: [], en: [], my: [] };
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const toArr = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);
            return { cn: toArr(parsed.cn), en: toArr(parsed.en), my: toArr(parsed.my) };
        }
        if (Array.isArray(parsed)) {
            return { cn: parsed.filter(Boolean), en: [], my: [] };
        }
        return { cn: [], en: [], my: [] };
    } catch {
        const lines = String(raw).split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        return { cn: lines, en: [], my: [] };
    }
}

function serializeVideoLinks(cnText, enText, myText) {
    const toArr = (t) => t.split('\n').map((s) => s.trim()).filter(Boolean);
    return JSON.stringify({ cn: toArr(cnText), en: toArr(enText), my: toArr(myText) });
}

function hydrateVideoTextareas(raw) {
    const { cn, en, my } = parseVideoLinks(raw);
    return {
        videoLinksCn: cn.join('\n'),
        videoLinksEn: en.join('\n'),
        videoLinksMy: my.join('\n'),
    };
}

const cardSx = {
    border: '1px solid #E2E8F0',
    borderRadius: '12px',
    p: { xs: 2, md: 2.5 },
    bgcolor: '#fff',
};

const cardHeadSx = {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#0F172A',
    fontFamily: PP,
    mb: 2,
    pb: 1,
    borderBottom: '1px solid #F1F5F9',
    letterSpacing: '0.2px',
};

const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP },
};

export default function TutorialFormPage() {
    const navigate = useNavigate();
    const { id } = useParams(); // undefined on /new
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY_FORM);
    const [tutorials, setTutorials] = useState([]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const coverRef = useRef(null);
    const attachRef = useRef(null);

    const categoriesUsed = useMemo(
        () => [...new Set(tutorials.map((t) => t.category).filter(Boolean))],
        [tutorials],
    );
    // Sub-topics are scoped to the chosen Topic (Category): only show
    // sub-categories that already exist UNDER the currently-selected topic.
    const subCategoriesUsed = useMemo(
        () => [...new Set(
            tutorials
                .filter((t) => t.category === form.category)
                .map((t) => t.subCategory)
                .filter(Boolean),
        )],
        [tutorials, form.category],
    );

    useEffect(() => {
        // Always load the full list (for category autocomplete + duplicate detection)
        getAllTutorials()
            .then((r) => {
                const list = r.data || [];
                setTutorials(list);
                if (isEdit) {
                    const raw = list.find((t) => String(t.tutorialId) === String(id));
                    if (raw) {
                        const videoTAs = hydrateVideoTextareas(raw.videoLinks);
                        setForm({
                            tutorialId: raw.tutorialId,
                            category: raw.category || '',
                            subCategory: raw.subCategory || '',
                            title: raw.title || '',
                            titleEn: raw.titleEn || '',
                            titleMy: raw.titleMy || '',
                            description: raw.description || '',
                            descriptionEn: raw.descriptionEn || '',
                            descriptionMy: raw.descriptionMy || '',
                            imageUrls: raw.imageUrls || '',
                            coverUrl: raw.coverUrl || '',
                            attachments: raw.attachments || '',
                            videoLinksCn: videoTAs.videoLinksCn,
                            videoLinksEn: videoTAs.videoLinksEn,
                            videoLinksMy: videoTAs.videoLinksMy,
                            sortOrder: raw.sortOrder ?? 0,
                        });
                    } else {
                        setError('Tutorial not found.');
                    }
                }
            })
            .catch(() => setError('Failed to load tutorials.'))
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    const similar = useMemo(
        () => findSimilarTitles(form.title, tutorials || [], { idKey: 'tutorialId', excludeId: form.tutorialId }),
        [form.title, tutorials, form.tutorialId],
    );

    const handleImageUpload = async (files) => {
        for (const file of files) {
            try {
                const res = await uploadTutorialImage(file);
                const newUrl = res.data.url;
                setForm((p) => {
                    const existing = p.imageUrls ? p.imageUrls.split('\n').filter(Boolean) : [];
                    return { ...p, imageUrls: [...existing, newUrl].join('\n') };
                });
            } catch (err) {
                alert('Upload failed: ' + (err.response?.data || err.message));
            }
        }
    };

    const handleCoverUpload = async (files) => {
        const file = files[0];
        if (!file) return;
        try {
            const res = await uploadTutorialImage(file);
            setForm((p) => ({ ...p, coverUrl: res.data.url }));
        } catch (err) {
            alert('Cover upload failed: ' + (err.response?.data || err.message));
        }
    };

    const handleAttachmentUpload = async (files) => {
        for (const file of files) {
            try {
                const res = await uploadTutorialFile(file);
                const newEntry = { url: res.data.url, name: res.data.fileName };
                setForm((p) => {
                    const existing = parseAttachments(p.attachments);
                    return { ...p, attachments: JSON.stringify([...existing, newEntry]) };
                });
            } catch (err) {
                alert('Attachment upload failed: ' + (err.response?.data || err.message));
            }
        }
    };

    const handleSave = async () => {
        if (!form.title.trim() || !form.category.trim()) {
            setError('Title (Chinese) and category are required.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const videoLinksJson = serializeVideoLinks(
                form.videoLinksCn,
                form.videoLinksEn,
                form.videoLinksMy,
            );
            const payload = {
                tutorialId: form.tutorialId,
                category: form.category,
                subCategory: form.subCategory || null,
                title: form.title,
                titleEn: form.titleEn || null,
                titleMy: form.titleMy || null,
                description: form.description || null,
                descriptionEn: form.descriptionEn || null,
                descriptionMy: form.descriptionMy || null,
                imageUrls: form.imageUrls || null,
                coverUrl: form.coverUrl || null,
                attachments: form.attachments || null,
                videoLinks: videoLinksJson,
                sortOrder: Number(form.sortOrder) || 0,
            };
            if (isEdit) {
                await updateTutorial(payload);
                navigate(`/tutorials?id=${id}`);
            } else {
                const res = await createTutorial(payload);
                const newId = res?.data?.tutorialId || res?.data?.id;
                navigate(newId ? `/tutorials?id=${newId}` : '/tutorials');
            }
        } catch (e) {
            setError('Failed to save. Please try again.');
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', fontFamily: PP, pb: 6 }}>
            {/* Page header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <IconButton
                    size="small"
                    onClick={() => navigate(isEdit ? `/tutorials?id=${id}` : '/tutorials')}
                    sx={{ color: '#64748B', border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.75 }}
                >
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, lineHeight: 1.2 }}>
                        {isEdit ? 'Edit Tutorial' : 'Add Tutorial'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: PP }}>
                        {isEdit ? `Editing tutorial #${id}` : 'Create a new tutorial entry'}
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Box sx={{ bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', p: 1.5, mb: 2 }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#DC2626' }}>{error}</Typography>
                </Box>
            )}

            {/* Two-column grid */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
            }}>

                {/* ── Organisation card — full width ── */}
                <Paper elevation={0} sx={{ ...cardSx, gridColumn: '1 / -1' }}>
                    <Typography sx={cardHeadSx}>Organisation</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Autocomplete
                            freeSolo options={categoriesUsed}
                            value={form.category || ''}
                            onInputChange={(_, v) => setForm((p) => ({ ...p, category: v || '' }))}
                            renderInput={(params) => (
                                <TextField {...params} label="Topic (Chinese)" size="small" required
                                    helperText="Pick from existing or type a new topic in Chinese"
                                    sx={fieldSx}
                                />
                            )}
                        />
                        <Autocomplete
                            freeSolo options={subCategoriesUsed}
                            value={form.subCategory || ''}
                            onInputChange={(_, v) => setForm((p) => ({ ...p, subCategory: v || '' }))}
                            renderInput={(params) => (
                                <TextField {...params} label="Subtopic (optional, Chinese)" size="small"
                                    helperText="Pick from existing or type a new subtopic in Chinese"
                                    sx={fieldSx}
                                />
                            )}
                        />
                        <TextField
                            label="Sort Order" size="small" type="number"
                            value={form.sortOrder}
                            onChange={(e) => setForm((p) => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))}
                            sx={{ width: 160, ...fieldSx }}
                        />
                    </Box>
                </Paper>

                {/* ── Chinese card ── */}
                <Paper elevation={0} sx={cardSx}>
                    <Typography sx={cardHeadSx}>Chinese</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Title (Chinese)" size="small" required
                            value={form.title}
                            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                            helperText="Required — e.g. 如何填写销售表格"
                            sx={fieldSx}
                        />
                        {similar.length > 0 && (
                            <Box sx={{ bgcolor: '#FEF3C7', borderLeft: '3px solid #F59E0B', borderRadius: '4px', p: 1, mt: -1 }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#92400E', fontFamily: PP, mb: 0.5 }}>
                                    Similar existing tutorials — check before adding:
                                </Typography>
                                {similar.map(({ item, sim }) => (
                                    <Typography key={item.tutorialId} sx={{ fontSize: '0.7rem', color: '#78350F', fontFamily: PP, pl: 1 }}>
                                        - {item.title} <span style={{ color: '#A8A29E' }}>(#{item.tutorialId} · {Math.round(sim * 100)}% match)</span>
                                    </Typography>
                                ))}
                            </Box>
                        )}
                        <TextField
                            label="Content (Chinese)" size="small" multiline rows={6}
                            value={form.description}
                            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            helperText="Step-by-step instructions in Chinese"
                            sx={fieldSx}
                        />
                        <TextField
                            label="Video URLs (Chinese, one per line)" size="small" multiline rows={2}
                            value={form.videoLinksCn}
                            onChange={(e) => setForm((p) => ({ ...p, videoLinksCn: e.target.value }))}
                            placeholder="https://youtu.be/abc123"
                            helperText="Paste full YouTube URLs for Chinese video"
                            sx={fieldSx}
                        />
                    </Box>
                </Paper>

                {/* ── English card ── */}
                <Paper elevation={0} sx={cardSx}>
                    <Typography sx={cardHeadSx}>English</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Title (EN)" size="small"
                            value={form.titleEn}
                            onChange={(e) => setForm((p) => ({ ...p, titleEn: e.target.value }))}
                            helperText="Optional — e.g. How to fill the sale form"
                            sx={fieldSx}
                        />
                        <TextField
                            label="Content (EN)" size="small" multiline rows={6}
                            value={form.descriptionEn}
                            onChange={(e) => setForm((p) => ({ ...p, descriptionEn: e.target.value }))}
                            helperText="Optional — English content"
                            sx={fieldSx}
                        />
                        <TextField
                            label="Video URLs EN (one per line)" size="small" multiline rows={2}
                            value={form.videoLinksEn}
                            onChange={(e) => setForm((p) => ({ ...p, videoLinksEn: e.target.value }))}
                            placeholder="https://youtu.be/abc123"
                            helperText="Optional — YouTube URLs for English video"
                            sx={fieldSx}
                        />
                    </Box>
                </Paper>

                {/* ── Bahasa Melayu card ── */}
                <Paper elevation={0} sx={cardSx}>
                    <Typography sx={cardHeadSx}>Bahasa Melayu</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Title (BM)" size="small"
                            value={form.titleMy}
                            onChange={(e) => setForm((p) => ({ ...p, titleMy: e.target.value }))}
                            helperText="Optional — e.g. Cara mengisi borang jualan"
                            sx={fieldSx}
                        />
                        <TextField
                            label="Content (BM)" size="small" multiline rows={6}
                            value={form.descriptionMy}
                            onChange={(e) => setForm((p) => ({ ...p, descriptionMy: e.target.value }))}
                            helperText="Optional — Bahasa Melayu content"
                            sx={fieldSx}
                        />
                        <TextField
                            label="Video URLs BM (one per line)" size="small" multiline rows={2}
                            value={form.videoLinksMy}
                            onChange={(e) => setForm((p) => ({ ...p, videoLinksMy: e.target.value }))}
                            placeholder="https://youtu.be/abc123"
                            helperText="Optional — YouTube URLs for Bahasa Melayu video"
                            sx={fieldSx}
                        />
                    </Box>
                </Paper>

                {/* Cover Photo is set per-topic via the Tutorials reader ("Edit cover"), not here. */}

                {/* ── Photos card — full width ── */}
                <Paper elevation={0} sx={{ ...cardSx, gridColumn: '1 / -1' }}>
                    <Typography sx={cardHeadSx}>Photos (optional)</Typography>
                    <input
                        type="file" ref={fileRef} hidden accept="image/*" multiple
                        onChange={(e) => { handleImageUpload(e.target.files); e.target.value = ''; }}
                    />
                    <Button
                        variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small"
                        onClick={() => fileRef.current?.click()}
                        sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px', mb: 1.5 }}
                    >
                        Upload Image
                    </Button>
                    {form.imageUrls && form.imageUrls.split('\n').filter(Boolean).map((url, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                            <img
                                src={resolveImageUrl(url)} alt=""
                                style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid #E2E8F0' }}
                            />
                            <Typography sx={{ fontFamily: PP, fontSize: '0.72rem', color: '#64748B', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {url}
                            </Typography>
                            <IconButton size="small" onClick={() => {
                                const urls = form.imageUrls.split('\n').filter(Boolean);
                                urls.splice(i, 1);
                                setForm((p) => ({ ...p, imageUrls: urls.join('\n') }));
                            }}>
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Box>
                    ))}
                </Paper>

                {/* ── Attachments card — full width ── */}
                <Paper elevation={0} sx={{ ...cardSx, gridColumn: '1 / -1' }}>
                    <Typography sx={cardHeadSx}>Attachments (optional)</Typography>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#64748B', mb: 1.5 }}>
                        PDFs, Word docs, or any file up to 20 MB. Shown as download links in the reader.
                    </Typography>
                    <input
                        type="file" ref={attachRef} hidden multiple
                        onChange={(e) => { handleAttachmentUpload(e.target.files); e.target.value = ''; }}
                    />
                    <Button
                        variant="outlined" component="span" startIcon={<AttachFileIcon />} size="small"
                        onClick={() => attachRef.current?.click()}
                        sx={{ fontFamily: PP, fontSize: '0.78rem', textTransform: 'none', borderRadius: '8px', mb: 1.5 }}
                    >
                        Attach File
                    </Button>
                    {parseAttachments(form.attachments).map((att, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                            <AttachFileIcon sx={{ fontSize: 16, color: '#64748B', flexShrink: 0 }} />
                            <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#334155', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {att.name}
                            </Typography>
                            <IconButton size="small" onClick={() => {
                                const list = parseAttachments(form.attachments);
                                list.splice(i, 1);
                                setForm((p) => ({ ...p, attachments: list.length ? JSON.stringify(list) : '' }));
                            }}>
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Box>
                    ))}
                </Paper>

                {/* ── Actions bar — full width ── */}
                <Box sx={{
                    gridColumn: '1 / -1',
                    pt: 1,
                    display: 'flex', gap: 1.5, justifyContent: 'flex-end',
                }}>
                    <Button
                        onClick={() => navigate(isEdit ? `/tutorials?id=${id}` : '/tutorials')}
                        sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.85rem', color: '#64748B' }}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon />}
                        disabled={saving || !form.title.trim() || !form.category.trim()}
                        sx={{
                            fontFamily: PP, textTransform: 'none', fontSize: '0.85rem', fontWeight: 600,
                            borderRadius: '8px', boxShadow: 'none',
                            background: 'linear-gradient(135deg,#1A8CFF,#0F62CC)',
                            '&.Mui-disabled': { background: '#E2E8F0' },
                        }}
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update' : 'Save')}
                    </Button>
                </Box>

            </Box>
        </Box>
    );
}
